/**
 * Avatar and user display utilities for chat.
 */

import { toAbsoluteUrl } from "src/resources/helpers"
import Customer from "src/app/models/Customer"
import { User } from "src/app/models/User"

type Person = User | Customer | undefined

const AVATAR_ICON_PATH = "/media/icons/avatar/"

/**
 * Get avatar URL for a user or customer.
 * Falls back to default avatar based on gender.
 */
export function getAvatarUrl(person: Person): string {
  const defaultAvatar = toAbsoluteUrl(`${AVATAR_ICON_PATH}def-avatar.png`)

  if (!person) {
    return defaultAvatar
  }

  if (person.avatar && person.avatar !== "") {
    return person.avatar
  }

  if (person.gender === "male") {
    return toAbsoluteUrl(`${AVATAR_ICON_PATH}m-avatar.png`)
  }

  if (person.gender === "female") {
    return toAbsoluteUrl(`${AVATAR_ICON_PATH}f-avatar.png`)
  }

  return defaultAvatar
}

// Legacy export name for backwards compatibility
export const avatarUrl = getAvatarUrl

/**
 * Get display name for a user.
 * @param person - User object
 * @param maxLength - Maximum string length (0 for no limit)
 */
export function getUserDisplayName(person: User | undefined, maxLength = 0): string {
  if (!person) {
    return "User Name"
  }

  let fullName = "User Name"

  if (person.name && person.name !== "") {
    fullName = person.name
  } else if (person.firstName && person.firstName !== "") {
    fullName = person.firstName
    if (person.lastName && person.lastName !== "") {
      fullName += " " + person.lastName
    }
  } else if (person.phoneNumber) {
    fullName = String(person.phoneNumber)
  }

  if (maxLength > 0 && fullName.length > maxLength) {
    fullName = fullName.substring(0, maxLength - 3) + "..."
  }

  return fullName
}

// Legacy export name for backwards compatibility
export const userName = getUserDisplayName

/**
 * Get display name for a customer.
 * @param customer - Customer object
 * @param maxLength - Maximum string length (0 for no limit)
 */
export function getCustomerDisplayName(customer: Customer | undefined, maxLength = 0): string {
  if (!customer) {
    return "Customer"
  }

  let fullName = "Customer"

  if (customer.firstName && customer.firstName !== "") {
    fullName = customer.firstName
    if (customer.lastName && customer.lastName !== "") {
      fullName += " " + customer.lastName
    }
  } else if (customer.phoneNumber) {
    fullName = `+${customer.phoneNumber}`
  }

  if (maxLength > 0 && fullName.length > maxLength) {
    fullName = fullName.substring(0, maxLength - 3) + "..."
  }

  return fullName
}
